import React, { CSSProperties, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { Box } from '@components/Box';
import { JoykitColor } from '@components/themes';
import { Columns } from '@components/Columns';
import { CTALink } from '@components/CTALink';
import { TextPairingProps } from '@components/TextPairing';
import {
  ColorVariant,
  PageVariant,
  getAbsolutePosition,
  getWebsiteArtVariant,
  getWebsiteFrameSrc,
  PlacementVariant,
  WebsiteFrameVariant,
} from './WeddingWebsite.variants';
import {
  FlexWrapper,
  LearnMoreWrapper,
  StyledCloudImage,
  TextPairingWrapper,
  WeddingWebsiteWrapper,
  FlexWrapperContainer,
  ColumnImageWrapper,
  ColumnInfoImageWrapper,
  StyledInfoImage,
  StyledImageWrapper,
  StyledMobileTextPairingWrapper,
  StyledSTDImageWrapper,
  StyledCardWrapper,
  StyledSTDTextWrapper,
  StyledImageArtsWrapper,
  SectionTextPairingWrapper,
  DesktopWrapper,
  MobileWrapper,
} from './WeddingWebsite.styles';
import { Button } from '@components/Button';
import { ButtonVariant } from '@components/Button/Button.styles';
import { LinkVariant } from '@components/themes';
import { SiteToSeeVideoSection } from '@sections/wedding-website/SiteToSeeNewSection';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';

interface WeddingWebsiteProps {
  frameVariant: WebsiteFrameVariant;
  colorVariant: ColorVariant;
  pageVariant: PageVariant;
  text: {
    eyebrow: string;
    title: string;
    mobileTitle?: string;
    body: string;
    mobileBody?: string;
    explore: string;
    learnMore: string;
  };
  btnVariant?: ButtonVariant;
  linkVariant?: LinkVariant;
  image?: string;
}

type artPlacementObject = {
  bottom: string;
  top: string;
  bottomLeft: string;
  bottomRight: string;
  topLeft: string;
  topRight: string;
};

const renderArts = (artVariant: artPlacementObject, frameVariant: WebsiteFrameVariant, pageVariant: PageVariant) => {
  const artPlacements = Object.keys(artVariant) as PlacementVariant[];

  return artPlacements.map((artPlacement, index) => {
    let style = {
      position: 'absolute',
      ...getAbsolutePosition(artPlacement as PlacementVariant, frameVariant, pageVariant),
    } as CSSProperties;
    return (
      <StyledImageArtsWrapper
        src={`sitewide/cross-sell_wedding-website/${pageVariant}/${artVariant[artPlacement]}`}
        alt={`art element ${artPlacement}`}
        width="100%"
        position={style.position}
        top={style.top}
        bottom={style.bottom}
        left={style.left}
        right={style.right}
        $imgWidth={style.width}
        zindex={style.zIndex}
        transform={style.transform}
        page={pageVariant}
        $artPlacement={artPlacement}
        key={index}
      />
    );
  });
};

const pageTextPairingProps: TextPairingProps = {
  eyebrowVariant: 'sectionEyeBrow20',
  eyebrowTagName: 'h2',
  titleVariant: 'hed56',
  bodyVariant: 'dek20',
  title: '',
  titleTagName: 'h2',
};

export const WeddingWebsite: React.FC<WeddingWebsiteProps> = ({
  frameVariant,
  colorVariant,
  text,
  pageVariant,
  btnVariant,
  linkVariant,
  image,
}) => {
  let artVariant = getWebsiteArtVariant(frameVariant, colorVariant, pageVariant);
  const backgroundColor = `${colorVariant}${pageVariant === 'guest-list' ? 'white' : '6'}` as JoykitColor;
  const eyebrowColor = pageVariant === 'guest-list' ? 'humanaDark4' : (`${colorVariant}3` as JoykitColor);
  const titleColor = pageVariant === 'guest-list' ? 'neutra6' : (`${colorVariant}2` as JoykitColor);
  const bodyColor = pageVariant === 'guest-list' ? 'neutra6' : (`${colorVariant}1` as JoykitColor);

  const pageVariantToSectionIdMap = {
    'guest-list': 'Guest_List_WW_Cross',
    rsvp: 'RSVP_Website_Cross',
    registry: 'Registry_Wedding_Website_Cross',
    'save-the-date': 'Save_the_Dates_Website_Cross',
  };

  const sectionId = pageVariantToSectionIdMap[pageVariant] || 'Guest_List_WW_Cross';
  const [isVisible, setIsVisible] = useState(false);
  const [isVisibleMobile, setIsVisibleMobile] = useState(false);

  if (pageVariant === 'rsvp' || pageVariant === 'registry') {
    return (
      <WeddingWebsiteWrapper backgroundColor={backgroundColor} paddingY={[14, '24rem']} page={pageVariant} id={sectionId}>
        <Box>{renderArts(artVariant as artPlacementObject, frameVariant, pageVariant)}</Box>
        <Columns>
          <Columns.Column width={6}>
            <StyledImageWrapper pageVariant={pageVariant}>
              <StyledCloudImage
                src={getWebsiteFrameSrc(frameVariant, colorVariant, pageVariant, image)}
                alt={`website frame ${frameVariant} ${colorVariant}`}
                mobileResizeWidth={600}
              />
            </StyledImageWrapper>
          </Columns.Column>
          <Columns.Column width={6}>
            <SectionTextPairingWrapper marginTop={[14, -14]} marginLeft={pageVariant === 'registry' ? [0, 12] : [0]}>
              <TextPairingWrapper
                {...pageTextPairingProps}
                eyebrow={text.eyebrow}
                eyebrowColor={eyebrowColor}
                title={ReactHtmlParser(text.title)}
                titleColor={titleColor}
                body={text.body}
                bodyColor={bodyColor}
                bodyMaxWidth={pageVariant === 'registry' ? '330px' : '460px'}
                alignment={['center', 'left']}
                page={pageVariant}
              />
              <StyledMobileTextPairingWrapper
                {...pageTextPairingProps}
                eyebrow={text.eyebrow}
                eyebrowColor={eyebrowColor}
                title={ReactHtmlParser(text.mobileTitle || text.title)}
                titleColor={titleColor}
                body={text.mobileBody || text.body}
                bodyColor={bodyColor}
                alignment={['center', 'left']}
                page={pageVariant}
              />
              <FlexWrapper alignItems={['center', 'left']}>
                <Box marginRight={[4, 8]}>
                  <Button id={`${pageVariant}ExploreLinkMore`} variant={btnVariant || 'secondary'} forwardedAs={'a'} href="/designs">
                    {text.explore}
                  </Button>
                </Box>
                <Box marginTop={[7, 0]}>
                  <LearnMoreWrapper
                    id="connected-learn-more"
                    href="/wedding-website"
                    typography={'featureHed20'}
                    variant={linkVariant}
                    hasChevron
                  >
                    {text.learnMore}
                  </LearnMoreWrapper>
                </Box>
              </FlexWrapper>
            </SectionTextPairingWrapper>
          </Columns.Column>
        </Columns>
      </WeddingWebsiteWrapper>
    );
  } else if (pageVariant === 'save-the-date') {
    return (
      <WeddingWebsiteWrapper backgroundColor={backgroundColor} paddingY={[9, 12]} page={pageVariant} id={sectionId}>
        <Box>{renderArts(artVariant as artPlacementObject, frameVariant, pageVariant)}</Box>
        <StyledSTDTextWrapper>
          <Columns.Column offset="one" width="ten" widescreenOffset="two" widescreen="eight">
            <SectionTextPairingWrapper>
              <TextPairingWrapper
                {...pageTextPairingProps}
                eyebrow={text.eyebrow}
                eyebrowColor={eyebrowColor}
                title={ReactHtmlParser(text.title)}
                titleColor={titleColor}
                body={ReactHtmlParser(text.body)}
                bodyColor={bodyColor}
                bodyMaxWidth="590px"
                alignment="center"
                marginY={10}
                page={pageVariant}
              />
              <StyledMobileTextPairingWrapper
                {...pageTextPairingProps}
                eyebrow={text.eyebrow}
                eyebrowColor={eyebrowColor}
                title={ReactHtmlParser(text.mobileTitle || text.title)}
                titleColor={titleColor}
                body={text.mobileBody || text.body}
                bodyColor={bodyColor}
                alignment={['center', 'left']}
                page={pageVariant}
              />
              <FlexWrapperContainer justifyContent="center" alignItems="center">
                <Box marginRight={[7]}>
                  <Button id="exploreLinkMore" variant={btnVariant || 'secondary'} forwardedAs={'a'} href="/designs">
                    {text.explore}
                  </Button>
                </Box>
                <Box marginTop={[7, 0]}>
                  <LearnMoreWrapper
                    id="connected-learn-more"
                    href="/wedding-website"
                    typography={'featureHed20'}
                    variant={linkVariant}
                    hasChevron
                  >
                    {text.learnMore}
                  </LearnMoreWrapper>
                </Box>
              </FlexWrapperContainer>
            </SectionTextPairingWrapper>
          </Columns.Column>
        </StyledSTDTextWrapper>
        <ColumnImageWrapper>
          <ColumnInfoImageWrapper width={4}>
            <StyledCardWrapper>
              <StyledInfoImage mobileResizeWidth={600} src="sitewide/cross-sell_wedding-website/save-the-date/std-gl.png" alt="image" />
            </StyledCardWrapper>
          </ColumnInfoImageWrapper>
          <Columns.Column width={8}>
            <StyledSTDImageWrapper>
              <StyledCloudImage
                mobileResizeWidth={600}
                src={getWebsiteFrameSrc(frameVariant, colorVariant, pageVariant)}
                alt={`website frame ${frameVariant} ${colorVariant}`}
              />
            </StyledSTDImageWrapper>
          </Columns.Column>
        </ColumnImageWrapper>
      </WeddingWebsiteWrapper>
    );
  }
  return (
    <WeddingWebsiteWrapper paddingY={[10, 12]} backgroundColor={backgroundColor} page={pageVariant} id={sectionId}>
      <DesktopWrapper>
        <LazyLoadWrapper beforeLoad={() => setIsVisible(true)}>
          <div />
        </LazyLoadWrapper>
      </DesktopWrapper>
      <MobileWrapper>
        <LazyLoadWrapper beforeLoad={() => setIsVisibleMobile(true)}>
          <div />
        </LazyLoadWrapper>
      </MobileWrapper>
      <Columns>
        <Columns.Column width={12}>
          <SectionTextPairingWrapper paddingBottom={[9, 11]}>
            <TextPairingWrapper
              {...pageTextPairingProps}
              eyebrow={text.eyebrow}
              eyebrowColor={eyebrowColor}
              title={ReactHtmlParser(text.title)}
              titleColor={titleColor}
              body={text.body}
              bodyColor={bodyColor}
              alignment="center"
              marginTop={10}
              marginBottom={5}
              page={pageVariant}
            />
            <StyledMobileTextPairingWrapper
              {...pageTextPairingProps}
              eyebrow={text.eyebrow}
              eyebrowColor={eyebrowColor}
              title={ReactHtmlParser(text.mobileTitle || text.title)}
              titleColor={titleColor}
              body={text.mobileBody || ''}
              bodyColor={bodyColor}
              alignment={['center', 'left']}
              page={pageVariant}
            />
            <FlexWrapperContainer justifyContent="center" alignItems="center">
              {pageVariant === 'guest-list' ? null : (
                <Box marginRight={[4, 8]}>
                  <Button id="exploreLinkMore" variant={btnVariant || 'secondary'} forwardedAs={'a'} href="/designs">
                    {text.explore}
                  </Button>
                </Box>
              )}
              <Box margin={[7, 0]} marginTop={0}>
                <CTALink
                  id="connected-learn-more"
                  aria-label="Wedding Website"
                  href="/wedding-website"
                  variant={linkVariant || 'primary'}
                  hasChevron={true}
                >
                  {text.learnMore}
                </CTALink>
              </Box>
            </FlexWrapperContainer>
          </SectionTextPairingWrapper>
        </Columns.Column>
      </Columns>
      <SiteToSeeVideoSection isVisible={isVisible} isVisibleMobile={isVisibleMobile} />
    </WeddingWebsiteWrapper>
  );
};
